import { FullPageSpinner } from "@/lib/components/ui/spinner";
import { useTenantsActions, useTenantsState } from "@frontegg/react";
import { log } from "@soulbyte/core";
import { useEffect, useState, type ReactNode } from "react";
import { OrgChooserScreen } from "./org-chooser-screen";
import { ORG_SELECTED_KEY } from "./org-selected-key";

/**
 * OrgSelectionGate blocks the app with a company chooser right after login when
 * the user belongs to more than one company. Once a company is picked (and the
 * Frontegg session switched into it), it renders the app.
 */
export function OrgSelectionGate({ children }: { children: ReactNode }) {
  const { tenants, activeTenant, loading } = useTenantsState();
  const { switchTenant, loadTenants } = useTenantsActions();

  const [chosen, setChosen] = useState(
    () => sessionStorage.getItem(ORG_SELECTED_KEY) !== null,
  );

  // ask Frontegg for the user's companies (the SDK authenticates this correctly)
  useEffect(
    function loadCompanyList() {
      if (!chosen) {
        loadTenants();
      }
    },
    [chosen, loadTenants],
  );

  // diagnostic: shows in the console how many companies the SDK reports, so we
  // can tell "single tenant" apart from "app-scoped to one tenant"
  useEffect(
    function logCompanyCount() {
      if (!chosen) {
        log(
          { event: "org_chooser_tenants_seen", count: tenants.length, loading },
          { level: "info" },
        );
      }
    },
    [chosen, tenants.length, loading],
  );

  if (chosen) {
    return <>{children}</>;
  }

  // wait until the company list has loaded (a signed-in user always has ≥1)
  if (loading || tenants.length === 0) {
    return <FullPageSpinner className="min-h-svh" />;
  }

  // one company — nothing to choose
  if (tenants.length <= 1) {
    return <>{children}</>;
  }

  // throws on a failed switch so OrgChooserScreen can show its error state and
  // keep the user's selection; on success the page reloads into the new company
  async function handleChoose(tenantId: string) {
    sessionStorage.setItem(ORG_SELECTED_KEY, tenantId);

    if (tenantId === activeTenant?.tenantId) {
      setChosen(true);
      return;
    }

    try {
      await switchTenant({ tenantId, silentReload: true });
      window.location.assign("/");
    } catch (error) {
      sessionStorage.removeItem(ORG_SELECTED_KEY);
      log.error(error, { disableTracking: true });
      throw error;
    }
  }

  return <OrgChooserScreen tenants={tenants} onChoose={handleChoose} />;
}
